import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBrandApple,
  IconBrandGoogle,
  IconBrandX,
  IconCheck,
  IconCompass,
  IconLock,
  IconMail,
  IconMapPin,
  IconScissors,
  IconX,
} from '@tabler/icons-react';
import type { AuthSessionStatus, OAuthProvider, PasswordResetPayload, RegistrationPolicy, RegisterPayload } from '@domain/auth/types';
import { LEGAL_LINKS } from '@shared/config/legal';
import { BrandLogo } from '@shared/ui/BrandLogo';
import { AuthDiscoveryBackdrop } from './AuthDiscoveryBackdrop';
import { AuthStoryPanel } from './AuthStoryPanel';

type AuthMode = 'login' | 'register' | 'forgot' | 'change';

export type AuthOAuthOption = {
  provider: OAuthProvider;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void | Promise<void>;
};

export type AuthScreenProps = {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  authStatus: AuthSessionStatus;
  authError: string | null;
  onLogin: (credentials: { email: string; password: string }) => void;
  onRegister: (payload: RegisterPayload) => Promise<void>;
  onResendRegistration: () => Promise<void>;
  onCompleteRegistration: (code: string, password: string) => Promise<void>;
  onRequestPasswordReset: (email: string) => Promise<void>;
  onSubmitPasswordReset: (payload: PasswordResetPayload) => Promise<void>;
  registrationPolicy: RegistrationPolicy;
  oauthOptions?: AuthOAuthOption[];
};

const passwordHint = '至少 6 位，包含大小写字母、数字和特殊字符';
const termsHref = LEGAL_LINKS.find((link) => link.label === '条款')?.href ?? '#';
const privacyHref = LEGAL_LINKS.find((link) => link.label === '隐私')?.href ?? '#';

const formInputStyles = {
  input: {
    backgroundColor: 'var(--locs-panel)',
    borderColor: 'var(--locs-border)',
    color: 'var(--locs-text)',
  },
  label: {
    color: 'var(--locs-text)',
    fontWeight: 600,
  },
};

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function validatePassword(value: string) {
  return value.length >= 6
    && /[A-Z]/.test(value)
    && /[a-z]/.test(value)
    && /\d/.test(value)
    && /[^A-Za-z0-9]/.test(value);
}

function authCopy(mode: AuthMode) {
  switch (mode) {
    case 'register':
      return {
        kicker: 'Join LocsClub',
        title: '加入专属脏辫网络',
        subtitle: '用内测邀请码获取邮件验证码，在此设置密码完成注册。',
        action: '发送验证码',
      };
    case 'forgot':
      return {
        kicker: 'Password Recovery',
        title: '找回密码',
        subtitle: '输入邮箱验证码，然后设置一个新的登录密码。',
        action: '重置密码',
      };
    case 'change':
      return {
        kicker: 'Account Security',
        title: '修改密码',
        subtitle: '通过邮箱验证码确认身份，然后设置新密码。',
        action: '保存新密码',
      };
    default:
      return {
        kicker: '',
        title: '喜欢 Locs 的人，在这里相遇',
        subtitle: '',
        action: '即刻开启探索',
      };
  }
}

function oauthIcon(provider: OAuthProvider) {
  switch (provider) {
    case 'apple':
      return <IconBrandApple size={18} />;
    case 'twitter':
      return <IconBrandX size={18} />;
    default:
      return <IconBrandGoogle size={18} />;
  }
}

export function AuthScreen(props: AuthScreenProps) {
  const {
    email,
    setEmail,
    password,
    setPassword,
    authStatus,
    authError,
    onLogin,
    onRegister,
    onResendRegistration,
    onCompleteRegistration,
    onRequestPasswordReset,
    onSubmitPasswordReset,
    registrationPolicy,
    oauthOptions = [],
  } = props;

  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [registrationPending, setRegistrationPending] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [isMapExploring, setIsMapExploring] = useState(false);
  const loginPasswordInputRef = useRef<HTMLInputElement | null>(null);

  const copy = useMemo(() => authCopy(mode), [mode]);
  const isBusy = authStatus === 'checking' || authStatus === 'submitting';
  const isResetMode = mode === 'forgot' || mode === 'change';
  const effectiveError = localError || authError;
  const visibleOAuthOptions = oauthOptions.filter((option) => registrationPolicy.oauthProviders.includes(option.provider));
  const canPasswordRegister = registrationPolicy.registrationEnabled && registrationPolicy.passwordRegistrationEnabled;
  const shouldShowOAuth = registrationPolicy.oauthLoginEnabled
    && visibleOAuthOptions.length > 0
    && (mode === 'login' || (mode === 'register' && registrationPolicy.oauthRegistrationEnabled));
  const readValue = (event: ChangeEvent<HTMLInputElement>) => event.currentTarget.value;

  useEffect(() => {
    if (mode === 'register' && !canPasswordRegister) {
      setMode('login');
    }
  }, [canPasswordRegister, mode]);

  const switchMode = (nextMode: AuthMode) => {
    if (isBusy) return;
    setMode(nextMode);
    setNotice(null);
    setLocalError(null);
    setConfirmPassword('');
    setInviteCode('');
    setRegistrationPending(false);
    setRegistrationComplete(false);
    setCode('');
    if (nextMode === 'login') {
      setPassword('');
    }
  };

  const requestCode = async () => {
    setNotice(null);
    setLocalError(null);
    if (!isEmail(email)) {
      setLocalError('请输入有效邮箱地址');
      return;
    }

    if ((mode === 'login' || mode === 'register') && !acceptedTerms) {
      setLocalError('请先阅读并同意用户协议与隐私条款');
      return;
    }
    try {
      await onRequestPasswordReset(email.trim());
      setNotice('验证码已发送到邮箱，请查收');
    } catch {
      /* auth state owns the visible error */
    }
  };

  const submit = async () => {
    setNotice(null);
    setLocalError(null);

    if (!isEmail(email)) {
      setLocalError('请输入有效邮箱地址');
      return;
    }

    if (mode === 'login') {
      const loginEmail = email.trim();
      const loginPassword = loginPasswordInputRef.current?.value ?? password;
      if (!loginPassword) {
        setLocalError('请输入密码');
        return;
      }
      onLogin({ email: loginEmail, password: loginPassword });
      return;
    }

    if (mode === 'register' && !registrationPending && name.trim().length < 2) {
      setLocalError('请输入至少 2 个字符的昵称');
      return;
    }

    if (mode === 'register' && !canPasswordRegister) {
      setLocalError('当前未开放密码注册');
      return;
    }

    if (mode === 'register' && !registrationPending && !inviteCode.trim()) {
      setLocalError('请输入邀请码');
      return;
    }

    if (isResetMode && !code.trim()) {
      setLocalError('请输入邮箱验证码');
      return;
    }

    if (mode === 'register') {
      if (!acceptedTerms) { setLocalError('请先阅读并同意用户协议与隐私条款'); return; }
      if (registrationPending) {
        if (!/^\d{6}$/.test(code)) { setLocalError('请输入 6 位邮箱验证码'); return; }
        if (!validatePassword(password)) { setLocalError(passwordHint); return; }
        if (password !== confirmPassword) { setLocalError('两次输入的密码不一致'); return; }
        try {
          await onCompleteRegistration(code, password);
          setRegistrationComplete(true);
          setPassword(''); setConfirmPassword(''); setCode('');
          setNotice('注册已完成，请返回登录页面登录');
        } catch { /* auth state owns the visible error */ }
        return;
      }
      try {
        await onRegister({ displayName: name.trim(), email: email.trim(), inviteCode: inviteCode.trim() });
        setRegistrationPending(true);
        setNotice('验证码已发送，请在此页面继续完成注册');
      } catch { /* auth state owns the visible error */ }
      return;
    }

    if (!validatePassword(password)) {
      setLocalError(passwordHint);
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('两次输入的密码不一致');
      return;
    }

    try {
      await onSubmitPasswordReset({ email: email.trim(), code: code.trim(), password, confirmPassword });
      setNotice(mode === 'change' ? '密码已修改，请重新登录' : '密码已重置，请重新登录');
      switchMode('login');
    } catch {
      /* auth state owns the visible error */
    }
  };

  const submitOAuth = async (option: AuthOAuthOption) => {
    setNotice(null);
    setLocalError(null);
    if (option.disabled) {
      setLocalError(option.disabledReason || `${option.label} 快捷登录尚未配置`);
      return;
    }
    try {
      await option.onClick();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Box className="auth-entry-screen" mih="100dvh">
      <AuthDiscoveryBackdrop interactive={isMapExploring} />
      <Box className="auth-entry-screen__overlay" data-exploring={isMapExploring ? 'true' : 'false'} aria-hidden="true" />
      <Box className="auth-entry-screen__pattern" aria-hidden="true" />

      <Box component="header" className="auth-entry-header">
        <Group gap="sm" wrap="nowrap">
          <Box className="auth-entry-header__logo"><BrandLogo size={48} /></Box>
          <Box>
            <Group gap={5} wrap="nowrap">
              <Text className="auth-entry-header__wordmark">locs<span>.club</span></Text>
              <Text className="auth-entry-header__launch">首发站</Text>
            </Group>
            <Text className="auth-entry-header__tagline">找到身边的手艺脏辫师</Text>
          </Box>
        </Group>
        <Group gap="sm" className="auth-entry-header__actions">
          <Group className="auth-entry-header__status" gap={7} wrap="nowrap" visibleFrom="sm">
            <IconMapPin size={14} />
            <Text size="xs">亚特兰大 · Atlanta, GA</Text>
          </Group>
          <Group className="auth-entry-header__status" gap={7} wrap="nowrap" visibleFrom="sm">
            <span className="auth-live-dot" />
            <Text size="xs">3 位脏辫师</Text>
          </Group>
          <Button
            className="auth-map-toggle"
            variant="subtle"
            size="xs"
            leftSection={isMapExploring ? <IconX size={15} /> : <IconCompass size={15} />}
            onClick={() => setIsMapExploring((value) => !value)}
            aria-pressed={isMapExploring}
          >
            {isMapExploring ? '返回登录' : '在地图上逛逛'}
          </Button>
        </Group>
      </Box>

      <Box
        component="main"
        className="auth-entry-main"
        data-hidden={isMapExploring ? 'true' : 'false'}
        aria-hidden={isMapExploring ? true : undefined}
        inert={isMapExploring}
      >
        <AuthStoryPanel />
        <Box className="auth-entry-form-column">
          <Stack gap="sm" mb={28}>
              <Group justify="space-between" align="center">
                <Box>
                  {copy.kicker && <Text size="xs" fw={800} c="terracotta.7" tt="uppercase">{copy.kicker}</Text>}
                  <Title order={1} fw={850} mt={4} c="var(--locs-text)" className="auth-entry-title">{copy.title}</Title>
                </Box>
                {mode !== 'login' && (
                  <ActionIcon className="auth-glass-panel__back" variant="subtle" radius="xl" size="lg" onClick={() => switchMode('login')} aria-label="返回登录">
                    <IconArrowLeft size={20} />
                  </ActionIcon>
                )}
              </Group>

              {copy.subtitle && (
                <Text c="var(--locs-text-muted)" size="sm" lh={1.65}>{copy.subtitle}</Text>
              )}

          </Stack>
          <Box
            className="auth-glass-panel"
            p={{ base: 22, sm: 30 }}
          >
            <Box className="auth-glass-panel__accent" aria-hidden="true" />
            <Stack gap="md" style={{ position: 'relative', zIndex: 1 }}>
              {effectiveError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} radius="md">{effectiveError}</Alert>
              )}
              {notice && (
                <Alert color="green" icon={<IconCheck size={16} />} radius="md">{notice}</Alert>
              )}

              {!registrationComplete && <Stack gap="sm">
                {mode === 'register' && !registrationPending && (
                  <TextInput
                    label="昵称"
                    value={name}
                    onChange={(event) => setName(readValue(event))}
                    placeholder="输入你的显示名称"
                    autoComplete="name"
                    size="md"
                    styles={formInputStyles}
                  />
                )}
                {mode === 'register' && !registrationPending && (
                  <TextInput
                    label="邀请码"
                    value={inviteCode}
                    onChange={(event) => setInviteCode(readValue(event))}
                    placeholder="内测邀请码"
                    autoComplete="off"
                    size="md"
                    styles={formInputStyles}
                  />
                )}
                <TextInput
                  label="邮箱"
                  value={email}
                  readOnly={mode === 'register' && registrationPending}
                  onChange={(event) => setEmail(readValue(event))}
                  placeholder="请输入邮箱地址"
                  autoComplete="username"
                  size="md"
                  leftSection={<IconMail size={16} color="var(--locs-text-muted)" />}
                  styles={formInputStyles}
                />
                {(isResetMode || (mode === 'register' && registrationPending)) && (
                  <Group gap="xs" align="flex-end" wrap="nowrap">
                    <TextInput
                      label="验证码"
                      value={code}
                      onChange={(event) => setCode(readValue(event))}
                      placeholder="6 位验证码"
                      autoComplete="one-time-code"
                      size="md"
                      style={{ flex: 1 }}
                      styles={formInputStyles}
                    />
                    {isResetMode && <Button variant="light" color="terracotta" size="md" onClick={() => void requestCode()} loading={isBusy} disabled={!email || isBusy}>发送</Button>}
                  </Group>
                )}
                {mode === 'login' ? (
                  <Box>
                    <Group justify="space-between" mb={5}>
                      <Text size="sm" fw={600} c="var(--locs-text)">密码</Text>
                      <Anchor size="xs" c="var(--locs-text-muted)" onClick={() => switchMode('forgot')}>忘记密码？</Anchor>
                    </Group>
                    <PasswordInput
                      aria-label="密码"
                      ref={loginPasswordInputRef}
                      defaultValue={password}
                      onChange={(event) => setPassword(readValue(event))}
                      placeholder="请输入密码"
                      autoComplete="current-password"
                      leftSection={<IconLock size={16} color="var(--locs-text-muted)" />}
                      leftSectionPointerEvents="none"
                      visibilityToggleButtonProps={{
                        color: 'var(--locs-text-muted)',
                        'aria-label': '显示或隐藏密码',
                      }}
                      size="md"
                      styles={formInputStyles}
                    />
                  </Box>
                ) : (isResetMode || (mode === 'register' && registrationPending)) ? (
                  <PasswordInput
                    label={mode === 'register' ? '设置密码' : '新密码'}
                    value={password}
                    onChange={(event) => setPassword(readValue(event))}
                    placeholder={passwordHint}
                    autoComplete="new-password"
                    size="md"
                    styles={formInputStyles}
                  />
                ) : null}
                {(isResetMode || (mode === 'register' && registrationPending)) && (
                  <PasswordInput
                    label="确认密码"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(readValue(event))}
                    placeholder="再次输入密码"
                    autoComplete="new-password"
                    size="md"
                    styles={formInputStyles}
                  />
                )}
                {(mode === 'login' || (mode === 'register' && !registrationPending)) && (
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.currentTarget.checked)}
                    color="terracotta"
                    size="xs"
                    className="auth-terms-checkbox"
                    label={(
                      <Text size="xs" c="var(--locs-text-muted)">
                        我已阅读并同意{' '}
                        <Anchor href={termsHref} target="_blank" rel="noreferrer" c="var(--locs-text)">用户协议</Anchor>
                        {' '}与{' '}
                        <Anchor href={privacyHref} target="_blank" rel="noreferrer" c="var(--locs-text)">隐私条款</Anchor>
                      </Text>
                    )}
                  />
                )}
              </Stack>}

              {!registrationComplete && <Button className="auth-entry-submit" size="lg" radius="md" color="terracotta" onClick={() => void submit()} disabled={isBusy} loading={authStatus === 'submitting'}>{registrationPending ? '完成注册' : copy.action}</Button>}
              {registrationPending && !registrationComplete && <Button variant="subtle" color="terracotta" onClick={() => void onResendRegistration().then(() => { setCode(''); setNotice('新验证码已发送，旧验证码已失效'); }).catch(() => setLocalError('暂时无法重发，请稍后重试'))} disabled={isBusy}>重新发送验证码</Button>}

              {shouldShowOAuth && (
                <Stack gap="sm">
                  <Group gap="sm" wrap="nowrap">
                    <Box h={1} bg="var(--locs-border)" style={{ flex: 1 }} />
                    <Text size="xs" fw={800} c="var(--locs-text-muted)" tt="uppercase">或使用快捷登录</Text>
                    <Box h={1} bg="var(--locs-border)" style={{ flex: 1 }} />
                  </Group>
                  <Group justify="center" gap="sm">
                    {visibleOAuthOptions.map((option) => (
                      <ActionIcon
                        key={option.provider}
                        className="auth-oauth-button"
                        variant="subtle"
                        radius="xl"
                        size={42}
                        onClick={() => void submitOAuth(option)}
                        disabled={isBusy}
                        title={`使用 ${option.label} 登录`}
                        aria-label={`使用 ${option.label} 登录`}
                      >
                        {oauthIcon(option.provider)}
                      </ActionIcon>
                    ))}
                  </Group>
                </Stack>
              )}

              {mode === 'login' ? (
                <Stack gap={8} align="center">
                  {canPasswordRegister && (
                    <Text size="sm" c="var(--locs-text-muted)">
                      还没有账号？{' '}
                      <Anchor component="button" type="button" disabled={isBusy} fw={800} color="terracotta" onClick={() => {
                        if (!canPasswordRegister) {
                          setLocalError('当前暂未开放账号注册，请稍后再试');
                          return;
                        }
                        switchMode('register');
                      }}>立即注册</Anchor>
                    </Text>
                  )}
                </Stack>
              ) : (
                <Text ta="center" size="sm" c="var(--locs-text-muted)">
                  已经准备好了？{' '}
                  <Anchor fw={800} color="terracotta" onClick={() => switchMode('login')}>返回登录</Anchor>
                </Text>
              )}
              <Group justify="center" gap={6} className="auth-loctician-note">
                <IconScissors size={15} />
                <Text size="xs">你是做脏辫的手艺人？登录后可免费入驻并装饰地图名片</Text>
              </Group>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Box component="footer" className="auth-entry-footer">
        <Text size="xs">首发城市：亚特兰大 · 纽约 · 休斯敦 · 洛杉矶</Text>
        <Text size="xs">© 2026 locs.club · 汇聚身边懂脏辫的手艺人</Text>
      </Box>
    </Box>
  );
}
